// src/pages/Register.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
    validateEmail,
    validateRequired,
    validatePassword,
    normalizeEmail,
    normalizePhone,
} from '../utils/validation';
import {
    COUNTRIES,
    PHONE_CODES,
    validatePhoneNumber,
    formatPhoneNumber,
    getPhoneExample,
    getPhoneCodeByCountryCode,
} from '../utils/phoneUtils';

export const Register: React.FC = () => {
    const navigate = useNavigate();
    const { user, signUp } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [selectedCountryCode, setSelectedCountryCode] = useState('TJ');
    const [selectedPhoneCode, setSelectedPhoneCode] = useState('+992');
    const [loading, setLoading] = useState(false);
    const isMountedRef = useRef(true);

    // Form fields
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Field-level errors
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (user && isMountedRef.current) {
            navigate('/', { replace: true });
        }
    }, [user, navigate]);

    // ── Validators ─────────────────────────────────────────────────────────

    const validateEmailField = (value: string): string => {
        if (!value) return 'Введите email';
        if (!validateEmail(value)) return 'Введите корректный email';
        return '';
    };

    const validatePhoneField = (value: string): string => {
        if (!value) return 'Введите номер телефона';
        const cleanPhone = normalizePhone(value);
        if (!validatePhoneNumber(cleanPhone, selectedPhoneCode)) {
            return `Неверный формат. Пример: ${getPhoneExample(selectedPhoneCode)}`;
        }
        return '';
    };

    const validatePasswordField = (value: string): string => {
        if (!value) return 'Введите пароль';
        const result = validatePassword(value);
        return result.error || '';
    };

    const validateConfirmPassword = (pass: string, confirm: string): string => {
        if (!confirm) return 'Повторите пароль';
        if (pass !== confirm) return 'Пароли не совпадают';
        return '';
    };

    // ── Handlers ──────────────────────────────────────────────────────────

    const handleCountryChange = (countryCode: string) => {
        setSelectedCountryCode(countryCode);
        const newPhoneCode = getPhoneCodeByCountryCode(countryCode);
        setSelectedPhoneCode(newPhoneCode);
        setPhone('');
        setErrors(prev => ({ ...prev, phone: '' }));
    };

    const handlePhoneCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedPhoneCode(e.target.value);
        setPhone('');
        setErrors(prev => ({ ...prev, phone: '' }));
    };

    const handlePhoneChange = (value: string) => {
        const formatted = formatPhoneNumber(value, selectedPhoneCode);
        setPhone(formatted);
        setErrors(prev => ({ ...prev, phone: validatePhoneField(formatted) }));
    };

    const handleEmailChange = (value: string) => {
        setEmail(value);
        setErrors(prev => ({ ...prev, email: validateEmailField(value) }));
    };

    const handlePasswordChange = (value: string) => {
        setPassword(value);
        setErrors(prev => ({
            ...prev,
            password: validatePasswordField(value),
            // Re-validate confirm if already touched
            ...(confirmPassword
                ? { confirmPassword: validateConfirmPassword(value, confirmPassword) }
                : {}),
        }));
    };

    const handleConfirmPasswordChange = (value: string) => {
        setConfirmPassword(value);
        setErrors(prev => ({ ...prev, confirmPassword: validateConfirmPassword(password, value) }));
    };

    // ── Submit ─────────────────────────────────────────────────────────────

    // src/pages/Register.tsx - исправленный handleRegister

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();

        const newErrors: Record<string, string> = {
            email: validateEmailField(email),
            firstName: validateRequired(firstName, 'Имя').error || '',
            lastName: validateRequired(lastName, 'Фамилия').error || '',
            phone: validatePhoneField(phone),
            password: validatePasswordField(password),
            confirmPassword: validateConfirmPassword(password, confirmPassword),
        };

        setErrors(newErrors);

        if (Object.values(newErrors).some(Boolean)) {
            toast.error('Пожалуйста, исправьте ошибки в форме');
            return;
        }

        const cleanPhone = normalizePhone(phone);

        setLoading(true);

        try {
            const result = await signUp({
                email: email.trim(),
                phone: cleanPhone,
                password,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phoneCode: selectedPhoneCode,
                countryCode: selectedCountryCode,
            });

            if (isMountedRef.current) {
                if (result.success) {
                    toast.success(
                        'Регистрация успешна! Письмо с подтверждением отправлено на ваш email.',
                        { duration: 4000 }
                    );

                    // ✅ ПОЛУЧАЕМ СОХРАНЕННЫЙ ПУТЬ ДЛЯ РЕДИРЕКТА
                    const redirectPath = sessionStorage.getItem('redirectAfterLogin');

                    // Небольшая задержка перед редиректом
                    setTimeout(() => {
                        if (redirectPath && redirectPath !== '/login' && redirectPath !== '/register') {
                            sessionStorage.removeItem('redirectAfterLogin');
                            navigate(redirectPath, { replace: true });
                        } else {
                            navigate('/login', { replace: true });
                        }
                    }, 2000);
                } else if (result.error) {
                    toast.error(result.error);
                }
            }
        } catch (error) {
            console.error('[Register] Error:', error);
            toast.error('Ошибка регистрации. Попробуйте позже.');
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h1 className="auth-title">Ruyo Market</h1>
                <p className="auth-subtitle">Создайте новый аккаунт</p>

                <form onSubmit={handleRegister} className="auth-form" noValidate>

                    {/* Email */}
                    <div className="form-group">
                        <input
                            type="email"
                            placeholder="Email *"
                            value={email}
                            onChange={(e) => handleEmailChange(e.target.value)}
                            className={`auth-input ${errors.email ? 'error' : ''}`}
                            autoComplete="email"
                        />
                        {errors.email && <div className="error-message">{errors.email}</div>}
                    </div>

                    {/* First / Last name */}
                    <div className="name-row">
                        <div className="form-group half">
                            <input
                                type="text"
                                placeholder="Имя *"
                                value={firstName}
                                onChange={(e) => {
                                    setFirstName(e.target.value);
                                    setErrors(prev => ({ ...prev, firstName: '' }));
                                }}
                                className={`auth-input ${errors.firstName ? 'error' : ''}`}
                                autoComplete="given-name"
                            />
                            {errors.firstName && (
                                <div className="error-message">{errors.firstName}</div>
                            )}
                        </div>
                        <div className="form-group half">
                            <input
                                type="text"
                                placeholder="Фамилия *"
                                value={lastName}
                                onChange={(e) => {
                                    setLastName(e.target.value);
                                    setErrors(prev => ({ ...prev, lastName: '' }));
                                }}
                                className={`auth-input ${errors.lastName ? 'error' : ''}`}
                                autoComplete="family-name"
                            />
                            {errors.lastName && (
                                <div className="error-message">{errors.lastName}</div>
                            )}
                        </div>
                    </div>

                    {/* Country of residence */}
                    <div className="form-group">
                        <label>Страна проживания</label>
                        <select
                            value={selectedCountryCode}
                            onChange={(e) => handleCountryChange(e.target.value)}
                            className="auth-input"
                        >
                            {COUNTRIES.map(country => (
                                <option key={country.code} value={country.code}>
                                    {country.flag} {country.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Phone code + number — combined row */}
                    <div className="form-group">
                        <label>Номер телефона *</label>
                        <div className="phone-input-group">
                            <select
                                value={selectedPhoneCode}
                                onChange={handlePhoneCodeChange}
                                className="country-code-select"
                                style={{ fontFamily: 'monospace', fontSize: '16px' }}
                            >
                                {PHONE_CODES.map((item) => (
                                    <option key={item.code} value={item.code}>
                                        {item.flag} {item.code}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                placeholder={getPhoneExample(selectedPhoneCode)}
                                value={phone}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className={`auth-input phone-input ${errors.phone ? 'error' : ''}`}
                                autoComplete="tel"
                            />
                        </div>
                        {errors.phone && <div className="error-message">{errors.phone}</div>}
                    </div>

                    {/* Password */}
                    <div className="form-group">
                        <div className="password-input-group">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Пароль *"
                                value={password}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                className={`auth-input ${errors.password ? 'error' : ''}`}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                            >
                                {showPassword
                                    ? <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                    : <EyeIcon style={{ width: 20, height: 20 }} />}
                            </button>
                        </div>
                        {errors.password && <div className="error-message">{errors.password}</div>}
                    </div>

                    {/* Confirm password */}
                    <div className="form-group">
                        <div className="password-input-group">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                placeholder="Повторите пароль *"
                                value={confirmPassword}
                                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                                className={`auth-input ${errors.confirmPassword ? 'error' : ''}`}
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                aria-label={showConfirmPassword ? 'Скрыть пароль' : 'Показать пароль'}
                            >
                                {showConfirmPassword
                                    ? <EyeSlashIcon style={{ width: 20, height: 20 }} />
                                    : <EyeIcon style={{ width: 20, height: 20 }} />}
                            </button>
                        </div>
                        {errors.confirmPassword && (
                            <div className="error-message">{errors.confirmPassword}</div>
                        )}
                    </div>

                    <button type="submit" disabled={loading} className="auth-button">
                        {loading ? 'Загрузка...' : 'Зарегистрироваться'}
                    </button>

                    <p className="info-text">
                        После регистрации вам будет отправлено письмо для подтверждения email
                    </p>
                </form>

                <div className="auth-switch">
                    <Link to="/login">Уже есть аккаунт? Войдите</Link>
                </div>
            </div>
        </div>
    );
};
